export const alphaPalette = [
  "#d50000",
  "#c51162",
  "#aa00ff",
  "#6200ea",
  "#304ffe",
  "#2962ff",
  "#0091ea",
  "#00b8d4",
  "#00bfa5",
  "#00c853",
  "#64dd17",
  "#aeea00",
  "#ffd600",
  "#ffab00",
  "#ff6d00",
  "#dd2c00",
  "#40241a",
  "#000000",
  "#616161",
  "#1c313a",
];

export const darkPalette = [
  "#b71c1c",
  "#ad1457",
  "#6a1b9a",
  "#4527a0",
  "#1a237e",
  "#0d47a1",
  "#007ac1",
  "#00838f",
  "#004d40",
  "#2e7d32",
  "#387002",
  "#827717",
  "#f57f17",
  "#ff6f00",
  "#bb4d00",
  "#b91400",
  "#6d4c41",
  "#4e342e",
  "#0f0f0f",
  "#37474f"
];

export const lightPalette = [
  "#ff8a80",
  "#ff80ab",
  "#ea80fc",
  "#b388ff",
  "#8c9eff",
  "#82b1ff",
  "#80d8ff",
  "#84ffff",
  "#a7ffeb",
  "#b9f6ca",
  "#ccff90",
  "#f4ff81",
  "#ffff8d",
  "#ffe57f",
  "#ffd180",
  "#ff9e80",
  "#bcaaa4",
  "#b0bec5",
  "#e0e0e0",
  "#eeeeee"
];

export const lightenPalette = [
  "#ffd4d9",
  "#f9c5d7",
  "#e5c8eb",
  "#d8cdec",
  "#ced2ec",
  "#c5e3fc",
  "#bee9fc",
  "#bdeef4",
  "#bde4e0",
  "#d0ead1",
  "#e1f0d0",
  "#f2f6cc",
  "#fffacd",
  "#ffefbe",
  "#ffe5bd",
  "#ffd4c6",
  "#e5dedb",
  "#e0e6e8",
  "#e9edef",
  "#f2f2f2"
];

export const futurePalette = [
  "#75BDE0",
  "#78D1D2",
  "#97DBAE",
  "#CDE4AD",
  "#3D9A8B",
  "#2EB19F",
  "#63C2D2",
  "#B7EDFF"
];

export const Themes = {
  light: {
    gray25: '#fafafa',
    gray50: '#fcfcfc',
    gray100: '#eeeeee',
    gray200: '#e0e0e0',
    gray300: '#d4d4d4',
    gray400: '#a3a3a3',
    gray500: '#878787',
    gray600: '#707070',
    gray700: '#595959',
    gray800: '#424242',
    gray900: '#212121'
  },
  dark: {
    gray25: '#171717',
    gray50: '#212121',
    gray100: '#373737',
    gray200: '#424242',
    gray300: '#595959',
    gray400: '#707070',
    gray500: '#878787',
    gray600: '#9e9e9e',
    gray700: '#b5b5b5',
    gray800: '#cccccc',
    gray900: '#e3e3e3'
  }
}

export const themeObserver = (target, callback) => {
  const weakTarget = new WeakRef(target);
  let observer = new MutationObserver((mutations) => {
    const currentTarget = weakTarget.deref();
    if (currentTarget) {
      mutations.forEach(() => {
        callback && callback();
      });
    } else {
      observer.disconnect();
    }
  });
  observer.observe(target, {
    attributes: true,
    attributeFilter: ['class']
  });
  return observer;
}
/* ------------------------------------------------------------------------------------
 * State tint (SS05902 ledger discipline — "calm density")
 *
 * A solid state chip is the loudest thing on a history row, for a fact that is often days
 * old. The calm-density variant keeps the state's own hue but spends it as a tint plus
 * text-grade ink of that same hue — the move the formula chips already made when they
 * dropped CSS `orange` for #b26a00.
 *
 * State colours are user-picked from the material-dark swatch set, and two of them
 * (yellow 700 #fbc02d, lime 700 #afb42b) are far too light to read as text. So the ink is
 * not a fixed mix: it is walked toward the surface's opposite until it clears WCAG AA
 * against the tinted background it will actually sit on. Both themes are computed here
 * because the tint is applied by CSS, which cannot do contrast maths.
 * ---------------------------------------------------------------------------------- */

export const STATE_TINT_ALPHA = 0.11;
const STATE_INK_MIN_CONTRAST = 4.5;
// the surface each themed chip composites over: the history panel's own background
const STATE_SURFACE = { light: [255, 255, 255], dark: [33, 33, 33] };

export const parseColor = (value) => {
  if (typeof value !== 'string') return null;

  const text = value.trim();
  const short = text.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (short) {
    return [short[1], short[2], short[3]].map((c) => parseInt(c + c, 16));
  }

  const long = text.match(/^#([0-9a-f]{6})$/i);
  if (long) {
    const int = parseInt(long[1], 16);
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
  }

  const fn = text.match(/^rgba?\(([^)]+)\)$/i);
  if (fn) {
    const parts = fn[1].split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(Number);
    if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
      return parts.map((n) => Math.min(255, Math.max(0, Math.round(n))));
    }
  }

  return null;
};

export const relativeLuminance = ([r, g, b]) => {
  const channel = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

export const contrastRatio = (a, b) => {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

// `fg` laid over `bg` at `alpha` — what the tinted chip background actually resolves to
export const blend = (fg, bg, alpha) =>
  fg.map((c, i) => Math.round(c * alpha + bg[i] * (1 - alpha)));

// Walk the hue toward `target` in 5% steps, stopping at the first shade that reads as text
// on `surface`. Returns the hue untouched when it already clears the bar.
const inkFor = (rgb, surface, target) => {
  for (let mix = 0; mix <= 1.0001; mix += 0.05) {
    const ink = rgb.map((c, i) => Math.round(c * (1 - mix) + target[i] * mix));
    if (contrastRatio(ink, surface) >= STATE_INK_MIN_CONTRAST) {
      return `rgb(${ink.join(', ')})`;
    }
  }
  return `rgb(${target.join(', ')})`;
};

/**
 * Text-grade shades of a state colour for the tinted chip, one per theme.
 * @param {string} color the state's configured colour
 * @returns {{light: string, dark: string}|null} null when the colour cannot be parsed
 */
export const stateInk = (color) => {
  const rgb = parseColor(color);
  if (!rgb) return null;

  return {
    light: inkFor(rgb, blend(rgb, STATE_SURFACE.light, STATE_TINT_ALPHA), [0, 0, 0]),
    dark: inkFor(rgb, blend(rgb, STATE_SURFACE.dark, STATE_TINT_ALPHA), [255, 255, 255])
  };
};
